import { useState, useEffect, useMemo } from "react";
import { CATEGORY_LABELS } from "../data/periodicTable.js";

// V62.5 — Multi-element compare view. Up to 4 elements pinnable.
// Opened from a "Compare (N)" floating launcher in PeriodicTable.jsx
// when 2+ elements are pinned.
//
// Behavior:
//   • Tabs: Identity, Periodic, Physical, Trends, Chemistry, All
//   • Default tab on first open: Identity
//   • "All" tab uses a customize-fields panel (checkboxes); selection
//     persists in localStorage. Other tabs show fixed field sets.
//   • Each pinned element renders as a column with its category color.
//   • X button on each column unpins that element.
//   • "Clear all" button to reset pins.
//
// State ownership:
//   • Pinned list lives in PeriodicTable.jsx (parent) — pinned-state
//     does NOT persist across page reloads, per spec.
//   • Selected tab is local to this component.
//   • All-tab custom field selection persists in localStorage under
//     key "nerdsuite.periodicCompare.fields".

// ─── Category coloring (mirrors PeriodicTable.jsx's CAT) ────────────
const CAT = {
  alkali:          { c: "#F87171" },
  alkaline:        { c: "#FB923C" },
  transition:      { c: "#FBBF24" },
  "post-transition": { c: "#34D399" },
  metalloid:       { c: "#22D3EE" },
  nonmetal:        { c: "#60A5FA" },
  halogen:         { c: "#A78BFA" },
  noble:           { c: "#F472B6" },
  lanthanide:      { c: "#2DD4BF" },
  actinide:        { c: "#C084FC" },
  unknown:         { c: "#6B7280" },
};

const EM = "#F8B84E";       // NerdChem accent (locked) — amber.
const EM_DEEP = "#7C3AED";  // NerdChem violet pair — used for accent strokes.
// Note: spec said "#A78BFA (violet)" but that's actually the Halogen
// category color (would clash with the halogen-tinted Cl/F/Br/I columns).
// Using EM_DEEP (#7C3AED) per the locked chem family pairing instead.

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// ─── Field definitions ─────────────────────────────────────────────
// Each field has: key (matches element prop), label, format function.
// format() takes the element and returns a display string, or "—" for
// missing values. Right-align-able fields are flagged numeric:true.

const FIELDS = {
  number:       { label: "Atomic number",  format: (el) => `${el.number}`, numeric: true },
  symbol:       { label: "Symbol",         format: (el) => el.symbol, numeric: false },
  name:         { label: "Name",           format: (el) => el.name, numeric: false },
  period:       { label: "Period",         format: (el) => `${el.period}`, numeric: true },
  group:        { label: "Group",          format: (el) => el.group == null ? "—" : `${el.group}`, numeric: true },
  block:        { label: "Block",          format: (el) => el.block, numeric: false },
  category:     { label: "Category",       format: (el) => el.categoryLabel || CATEGORY_LABELS[el.category] || "Unknown", numeric: false },
  mass:         {
    label: "Atomic mass",
    format: (el) => Array.isArray(el.mass) ? `[${el.mass[0]}]` : `${el.mass}`,
    numeric: true,
  },
  electroneg:        { label: "Electronegativity (Pauling)", format: (el) => el.electroneg == null ? "—" : `${el.electroneg}`, numeric: true },
  atomicRadius:      { label: "Atomic radius",      format: (el) => el.atomicRadius == null ? "—" : `${el.atomicRadius} pm`, numeric: true },
  commonCharges:     { label: "Common ionic charges", format: (el) => el.commonCharges || "—", numeric: false },
  commonOxidationStates: { label: "Common oxidation states", format: (el) => el.commonOxidationStates || "—", numeric: false },
  config:            { label: "Electron configuration", format: (el) => el.config, numeric: false },
  state:             { label: "State at STP",       format: (el) => el.state === "unknown" ? "—" : el.state.charAt(0).toUpperCase() + el.state.slice(1), numeric: false },
  density:           { label: "Density",            format: (el) => el.density == null ? "—" : `${el.density} g/cm³`, numeric: true },
  melt:              { label: "Melting point",      format: (el) => el.melt == null ? "—" : `${el.melt} K`, numeric: true },
  boil:              { label: "Boiling point",      format: (el) => el.boil == null ? "—" : `${el.boil} K`, numeric: true },
  electronAffinity:  { label: "Electron affinity",  format: (el) => el.electronAffinity == null ? "—" : `${el.electronAffinity} kJ/mol`, numeric: true },
  ionizationEnergy:  { label: "Ionization energy",  format: (el) => el.ionizationEnergy == null ? "—" : `${el.ionizationEnergy} kJ/mol`, numeric: true },
  discovered:        { label: "Discovery year",     format: (el) => `${el.discovered}`, numeric: false },
  discoveredBy:      { label: "Discovered by",      format: (el) => el.discoveredBy || "—", numeric: false },
};

// Canonical field order (used in All-tab + as the source order for
// the checkbox-picker list). Identity-first → defining physical →
// bonding/chemistry → physical specifics → context.
const ALL_FIELDS_ORDER = [
  "number", "symbol", "name",
  "period", "group", "block", "category",
  "mass",
  "electroneg", "atomicRadius",
  "commonCharges", "commonOxidationStates", "config",
  "state", "density", "melt", "boil",
  "electronAffinity", "ionizationEnergy",
  "discovered", "discoveredBy",
];

// Tab definitions.
const TABS = [
  { id: "identity", label: "Identity",  fields: ["number", "symbol", "name", "category", "discovered", "discoveredBy"] },
  { id: "periodic", label: "Periodic",  fields: ["period", "group", "block", "category", "config"] },
  { id: "physical", label: "Physical",  fields: ["state", "mass", "density", "melt", "boil"] },
  { id: "trends",   label: "Trends",    fields: ["electroneg", "atomicRadius", "electronAffinity", "ionizationEnergy"] },
  { id: "chemistry", label: "Chemistry", fields: ["commonCharges", "commonOxidationStates", "config"] },
  { id: "all",      label: "All",       fields: null }, // special — uses checkbox selection
];

// Defaults for the All tab on first visit, per spec.
const DEFAULT_ALL_FIELDS = ["symbol", "name", "number", "mass", "commonCharges", "category"];

// localStorage key for All-tab field persistence.
const LS_KEY = "nerdsuite.periodicCompare.fields";

// ─── Component ──────────────────────────────────────────────────────
export default function PeriodicCompare({ pinned, onUnpin, onClear, onClose }) {
  const [tab, setTab] = useState("identity");
  const [allFields, setAllFields] = useState(DEFAULT_ALL_FIELDS);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Load persisted field selection on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every((k) => typeof k === "string")) {
          // Filter to only-currently-defined fields (in case a stored key
          // refers to a field that's since been removed).
          const valid = parsed.filter((k) => k in FIELDS);
          if (valid.length > 0) setAllFields(valid);
        }
      }
    } catch (_e) {
      // Bad localStorage value — fall back to defaults silently.
    }
  }, []);

  // Persist on change.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(allFields));
    } catch (_e) { /* quota or disabled — ignore */ }
  }, [allFields]);

  // Compute the active field list for the current tab.
  const activeFields = useMemo(() => {
    if (tab === "all") {
      // Preserve canonical order regardless of selection order
      return ALL_FIELDS_ORDER.filter((k) => allFields.includes(k));
    }
    const def = TABS.find((t) => t.id === tab);
    return def ? def.fields : [];
  }, [tab, allFields]);

  // Trap Escape to close the modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (pickerOpen) setPickerOpen(false);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pickerOpen, onClose]);

  if (!pinned || pinned.length === 0) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Element comparison"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "stretch", justifyContent: "center",
        zIndex: 200,
        padding: "clamp(0px, 4vw, 40px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0E1019",
          border: `1px solid ${rgba(EM_DEEP, 0.35)}`,
          borderRadius: 18,
          width: "100%", maxWidth: 980,
          maxHeight: "100%",
          display: "flex", flexDirection: "column",
          boxShadow: `0 20px 60px ${rgba(EM_DEEP, 0.18)}`,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px",
          borderBottom: `1px solid ${rgba(EM_DEEP, 0.15)}`,
          background: "#0A0B14",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              fontSize: 17, fontWeight: 700, color: "#E2E8F0",
            }}>Compare elements</span>
            <span style={{ fontSize: 12, color: "#6B7280" }}>{pinned.length} pinned</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onClear}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#9CA3AF",
                fontSize: 12, fontFamily: "inherit",
                padding: "6px 11px", borderRadius: 8, cursor: "pointer",
              }}
            >Clear all</button>
            <button
              onClick={onClose}
              aria-label="Close compare view"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "none", color: "#9CA3AF",
                width: 32, height: 32, borderRadius: 8, cursor: "pointer",
                fontSize: 16, fontFamily: "inherit",
              }}
            >✕</button>
          </div>
        </header>

        {/* Tab bar */}
        <nav role="tablist" aria-label="Compare view tabs" style={{
          display: "flex", overflowX: "auto",
          borderBottom: `1px solid ${rgba(EM_DEEP, 0.12)}`,
          background: "#0A0B14",
        }}>
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: active ? `2px solid ${EM}` : "2px solid transparent",
                  color: active ? EM : "#9CA3AF",
                  fontFamily: "inherit", fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  padding: "11px 16px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >{t.label}</button>
            );
          })}
        </nav>

        {/* All-tab customize-fields button */}
        {tab === "all" && (
          <div style={{
            padding: "8px 18px",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#0A0B14",
          }}>
            <span style={{ fontSize: 12, color: "#6B7280" }}>
              {allFields.length} field{allFields.length === 1 ? "" : "s"} selected
            </span>
            <button
              onClick={() => setPickerOpen(true)}
              style={{
                background: rgba(EM_DEEP, 0.15),
                border: `1px solid ${rgba(EM_DEEP, 0.4)}`,
                color: "#E2E8F0",
                fontFamily: "inherit", fontSize: 12, fontWeight: 600,
                padding: "6px 12px", borderRadius: 8, cursor: "pointer",
              }}
            >Customize fields</button>
          </div>
        )}

        {/* Body — element columns */}
        <div style={{ flex: 1, overflow: "auto", padding: "16px 18px 20px" }}>
          <CompareTable elements={pinned} fields={activeFields} onUnpin={onUnpin} />
        </div>
      </div>

      {/* Field picker bottom sheet */}
      {pickerOpen && (
        <FieldPicker
          selected={allFields}
          onChange={setAllFields}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ─── CompareTable — the actual grid of element columns ─────────────
function CompareTable({ elements, fields, onUnpin }) {
  return (
    <div style={{
      display: "grid",
      // Field-label column + one column per element
      gridTemplateColumns: `minmax(140px, 200px) repeat(${elements.length}, minmax(110px, 1fr))`,
      gap: 0,
      fontSize: 13,
      width: "100%",
    }}>
      {/* Column headers: empty cell + element heads */}
      <div />
      {elements.map((el) => {
        const cat = CAT[el.category] || CAT.unknown;
        return (
          <div
            key={el.symbol}
            aria-label={`${el.name} data`}
            style={{
              background: rgba(cat.c, 0.15),
              borderTop: `2px solid ${cat.c}`,
              padding: "10px 8px 12px",
              textAlign: "center",
              position: "relative",
              minWidth: 0,
            }}
          >
            <button
              onClick={() => onUnpin(el.symbol)}
              aria-label={`Unpin ${el.name}`}
              style={{
                position: "absolute", top: 4, right: 4,
                background: "rgba(0,0,0,0.3)",
                border: "none", color: "#E2E8F0",
                width: 20, height: 20, borderRadius: 5, cursor: "pointer",
                fontSize: 11, fontFamily: "inherit", lineHeight: 1,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >✕</button>
            <div style={{ fontSize: 10, color: cat.c, fontWeight: 600 }}>{el.number}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>{el.symbol}</div>
            <div style={{ fontSize: 11, color: "#E2E8F0", marginTop: 2 }}>{el.name}</div>
          </div>
        );
      })}

      {/* Field rows */}
      {fields.map((fieldKey, rowIdx) => {
        const f = FIELDS[fieldKey];
        if (!f) return null;
        return (
          <FieldRow
            key={fieldKey}
            label={f.label}
            isOdd={rowIdx % 2 === 1}
            cells={elements.map((el) => ({
              text: f.format(el),
              numeric: f.numeric,
              key: el.symbol,
            }))}
          />
        );
      })}
    </div>
  );
}

function FieldRow({ label, cells, isOdd }) {
  const bg = isOdd ? "rgba(255,255,255,0.02)" : "transparent";
  return (
    <>
      <div style={{
        padding: "10px 8px",
        fontSize: 11, color: "#6B7280", fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.4px",
        background: bg,
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        display: "flex", alignItems: "center",
      }}>{label}</div>
      {cells.map((c) => (
        <div
          key={c.key}
          style={{
            padding: "10px 8px",
            fontSize: 13, color: "#E2E8F0",
            background: bg,
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            textAlign: c.numeric ? "right" : "left",
            fontVariantNumeric: c.numeric ? "tabular-nums" : "normal",
            wordBreak: "break-word",
          }}
        >{c.text}</div>
      ))}
    </>
  );
}

// ─── FieldPicker — bottom-sheet (mobile) / modal (desktop) ──────────
function FieldPicker({ selected, onChange, onClose }) {
  const [draft, setDraft] = useState(selected);

  const toggle = (key) => {
    setDraft((cur) => cur.includes(key)
      ? cur.filter((k) => k !== key)
      : [...cur, key]);
  };

  const done = () => {
    onChange(draft);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Customize fields"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 220,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#11131F",
          border: `1px solid ${rgba(EM_DEEP, 0.35)}`,
          borderTopLeftRadius: 18, borderTopRightRadius: 18,
          width: "100%", maxWidth: 520,
          maxHeight: "75vh",
          display: "flex", flexDirection: "column",
          boxShadow: `0 -20px 60px ${rgba(EM_DEEP, 0.25)}`,
        }}
      >
        <header style={{
          padding: "14px 18px",
          borderBottom: `1px solid ${rgba(EM_DEEP, 0.15)}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#E2E8F0" }}>
            Customize fields
          </h3>
          <span style={{ fontSize: 12, color: "#6B7280" }}>
            {draft.length} selected
          </span>
        </header>

        <div style={{ flex: 1, overflow: "auto", padding: "10px 18px" }}>
          {ALL_FIELDS_ORDER.map((key) => {
            const f = FIELDS[key];
            if (!f) return null;
            const checked = draft.includes(key);
            return (
              <label
                key={key}
                style={{
                  display: "flex", alignItems: "center", gap: 11,
                  padding: "9px 4px",
                  cursor: "pointer",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(key)}
                  aria-label={f.label}
                  style={{
                    width: 16, height: 16, accentColor: EM,
                    cursor: "pointer",
                  }}
                />
                <span style={{
                  fontSize: 14, color: checked ? "#E2E8F0" : "#9CA3AF",
                  fontWeight: checked ? 500 : 400,
                }}>{f.label}</span>
              </label>
            );
          })}
        </div>

        <footer style={{
          padding: "12px 18px",
          borderTop: `1px solid ${rgba(EM_DEEP, 0.15)}`,
          display: "flex", justifyContent: "flex-end",
        }}>
          <button
            onClick={done}
            style={{
              background: EM, border: "none", color: "#1A0604",
              fontWeight: 700, fontSize: 13, fontFamily: "inherit",
              padding: "9px 18px", borderRadius: 8, cursor: "pointer",
            }}
          >Done</button>
        </footer>
      </div>
    </div>
  );
}

// ─── Export the FIELDS dict for tests and external use ──────────────
export const __FIELDS = FIELDS;
export const __TABS = TABS;
export const __ALL_FIELDS_ORDER = ALL_FIELDS_ORDER;
export const __DEFAULT_ALL_FIELDS = DEFAULT_ALL_FIELDS;
export const __LS_KEY = LS_KEY;
